import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { planDurations, getFeatureIdFromName } from './data/OnboardingData';
import { useCurrency } from '../../context-v2/CurrencyContext';
import { validatePlanCreationForm } from './validations/OnboardingValidations';
import { convertToINR } from '../common/CurrencyUtils';
import SelectDropdownLabel from '../../components/components-react/SelectDropdownLabel';
import CurrencyDisplay from '../common/CurrencyDisplay';
import MultiSelectV2 from '../common/MultiSelectV2';
import { commonPostPutApi } from '../../redux/reducers/commonApiSlice';
import { fetchDashboardFeaturesdata } from '../../redux/dashboard-features/featuresSlice';
import '../../styles-v2/onboarding/v2-onboarding.css';
import '../../styles-v2/forms/v2-select-dropdown.css';

const PlanCreationForm = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);
  const { dashboardFeatures, dashboardFeaturesLoading } = useSelector(
    (state) => state.features
  );
  const { currency } = useCurrency();

  const formRef = useRef({});
  const [planFeatures, setPlanFeatures] = useState([]);

  const iniFormValues = {
    planName: '',
    description: '',
    duration: "",
    maxMembers: '',
    maxGyms: '',
    basePrice: '',
    validFrom: '',
    validTo: '',
    isActive: true,
    selectedFeatures: [],
  };
  const [formValues, setFormValues] = useState(iniFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const handleFeaturesChange = (selectedOptions) => {
    setFormValues((prev) => ({
      ...prev,
      selectedFeatures: selectedOptions.map((opt) => opt.value),
    }));
    setIsSubmit(false);
  };

  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;
    setIsSubmit(false);

    setFormValues((prevs) => ({
      ...prevs,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onChangeDropdown = (value, name) => {
    setIsSubmit(false);
    setFormValues((prevs) => ({
      ...prevs,
      [name]: value,
    }));
  };

  const assignRef = (fieldName) => (el) => {
    if (!formRef.current) {
      formRef.current = {};
    }

    if (el) {
      formRef.current[fieldName] = el;
    }
  };

  const getDashboardFeatures = useCallback(() => {
    const filters = {
      page: 1,
      limit: 1000,
      status: 1,
    };

    dispatch(
      fetchDashboardFeaturesdata({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }, [dispatch, userInfoData?.token]);

  useEffect(() => {
    getDashboardFeatures();
  }, [getDashboardFeatures]);

  useEffect(() => {
    if (dashboardFeatures?.status && dashboardFeatures?.data?.length > 0) {
      const features = dashboardFeatures.data.map((feature) => {
        const featureId = getFeatureIdFromName(feature?.name);
        return {
          id: feature?.uid || '',
          label: feature?.name || '',
          iconId: featureId,
        };
      });
      setPlanFeatures(features);
    } else {
      setPlanFeatures([]);
    }
  }, [dashboardFeatures]);

  const handleSubmitPlan = async (isDraft = 0) => {
    const errors = validatePlanCreationForm(formValues);
    setFormErrors(errors);
    setIsSubmit(true);

    if (Object.keys(errors).length > 0) {
      if (formRef.current) {
        const firstErrorField = Object.keys(errors).find((field) => formRef.current[field]);

        if (firstErrorField) {
          formRef.current[firstErrorField].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
      toast.error(
        isDraft ? 'Please fix the errors before saving' : 'Please fix the errors before publishing'
      );
      return;
    }

    // Convert price to INR for backend (always store in INR regardless of display currency)
    const payload = {
      plan_name: formValues.planName,
      description: formValues.description,
      duration: formValues.duration?.value || 'Monthly',
      max_members: formValues.maxMembers,
      max_gyms: formValues.maxGyms,
      base_price: convertToINR(formValues.basePrice, currency),
      valid_from: formValues.validFrom,
      valid_to: formValues.validTo,
      is_active: isDraft ? false : formValues.isActive,
      features: formValues.selectedFeatures,
      created_by: userInfoData?.uid || userInfoData?.user_id || '',
      is_draft: isDraft,
    };

    const method = 'POST';
    const token = userInfoData?.token || '';
    const endpoint = '/';

    try {
      const result = await dispatch(commonPostPutApi({ method, endpoint, payload, token }));

      if (result?.payload?.status) {
        setIsSubmit(false);
        setFormValues(iniFormValues);

        toast.success(
          isDraft
            ? 'Plan saved as draft successfully!'
            : result?.payload?.message || 'Plan published successfully!'
        );
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || 'Something went wrong!');
      }
    } catch (error) {
      console.error('Error making POST request:', error);
      toast.error('Failed to submit form');
    }
  };

  return (
    <div className="v2-plan-creation">
      <div className="v2-plan-creation__container">
        <div className="v2-plan-creation__form">
          <div className="v2-plan-creation__header">
            <h2 className="v2-plan-creation__title">
              <span className="v2-plan-creation__icon">+</span>
              Create New Plan
            </h2>
            <p className="v2-plan-creation__description">
              Define subscription plans with features, limits, and pricing
            </p>
          </div>

          <div className="v2-plan-creation__row">
            <div className="v2-form-group" ref={assignRef('planName')}>
              <label className="v2-form-label">
                Plan Name <span className="v2-form-required">*</span>
              </label>
              <input
                type="text"
                className={`v2-form-input ${formErrors.planName ? 'error' : ''}`}
                placeholder="e.g., Starter, Pro, Enterprise"
                name="planName"
                value={formValues.planName}
                onChange={onChangeInput}
              />
              {formErrors.planName && <span className="error-label">{formErrors.planName}</span>}
            </div>

            <div className="v2-form-group" ref={assignRef('duration')}>
              <label className="v2-form-label">
                Plan Duration <span className="v2-form-required">*</span>
              </label>

              <div
                className={`v2-select-dropdown-wrapper ${formErrors.duration ? 'v2-select-dropdown-wrapper--error' : ''}`}
              >
                <SelectDropdownLabel
                  options={planDurations}
                  selectedOption={formValues.duration}
                  setSelectedOption={(selectedValue) => onChangeDropdown(selectedValue, 'duration')}
                  name="Duration"
                  classNamePrefix="react-select"
                />
              </div>
              {formErrors.duration && <span className="error-label">{formErrors.duration}</span>}
            </div>
          </div>

          <div className="v2-form-group">
            <label className="v2-form-label">Description</label>
            <textarea
              className="v2-form-textarea"
              placeholder="Describe what this plan offers..."
              rows="4"
              name="description"
              value={formValues.description}
              onChange={onChangeInput}
            />
          </div>

          <div className="v2-form-group" ref={assignRef('selectedFeatures')}>
            <label className="v2-form-label">
              Select Features <span className="v2-form-required">*</span>
            </label>

            {dashboardFeaturesLoading ? (
              <div className="v2-form-loading">
                <span className="v2-form-loading-text">Loading features...</span>
              </div>
            ) : (
              <MultiSelectV2
                options={planFeatures.map((f) => ({ label: f.label, value: f.id }))}
                value={planFeatures
                  .filter((f) => formValues.selectedFeatures.includes(f.id))
                  .map((f) => ({ label: f.label, value: f.id }))}
                onChange={handleFeaturesChange}
                name="Features"
                placeholder="Select features for this plan"
                isDisabled={dashboardFeaturesLoading}
                maxChipsDisplay={7}
                error={!!formErrors.selectedFeatures}
              />
            )}

            {formErrors.selectedFeatures && (
              <span className="error-label">{formErrors.selectedFeatures}</span>
            )}
          </div>

          <div className="v2-plan-creation__row v2-plan-creation__row--three">
            <div className="v2-form-group" ref={assignRef('maxMembers')}>
              <label className="v2-form-label">
                Max Members <span className="v2-form-required">*</span>
              </label>
              <input
                type="number"
                className={`v2-form-input ${formErrors.maxMembers ? 'error' : ''}`}
                placeholder="e.g., 100, 500, 1000"
                name="maxMembers"
                value={formValues.maxMembers}
                onChange={onChangeInput}
                min="1"
              />
              {formErrors.maxMembers && (
                <span className="error-label">{formErrors.maxMembers}</span>
              )}
            </div>
            <div className="v2-form-group" ref={assignRef('maxGyms')}>
              <label className="v2-form-label">
                Max Gyms <span className="v2-form-required">*</span>
              </label>
              <input
                type="number"
                className={`v2-form-input ${formErrors.maxGyms ? 'error' : ''}`}
                placeholder="e.g., 1, 5, 10"
                name="maxGyms"
                value={formValues.maxGyms}
                onChange={onChangeInput}
                min="1"
              />
              {formErrors.maxGyms && <span className="error-label">{formErrors.maxGyms}</span>}
            </div>

            <div className="v2-form-group" ref={assignRef('basePrice')}>
              <label className="v2-form-label">
                Base Price (<CurrencyDisplay symbolOnly />) <span className="v2-form-required">*</span>
              </label>
              <div className="v2-form-input-prefix">
                <span className="v2-form-prefix">
                  <CurrencyDisplay symbolOnly />
                </span>
                <input
                  type="number"
                  className={`v2-form-input v2-form-input--with-prefix ${formErrors.basePrice ? 'error' : ''}`}
                  placeholder="Enter amount"
                  name="basePrice"
                  value={formValues.basePrice}
                  onChange={onChangeInput}
                  min="0"
                />
              </div>
              {formErrors.basePrice && <span className="error-label">{formErrors.basePrice}</span>}
            </div>
          </div>

          <div className="v2-plan-creation__row">
            <div className="v2-form-group" ref={assignRef('validFrom')}>
              <label className="v2-form-label">
                Valid From <span className="v2-form-required">*</span>
              </label>
              <input
                type="date"
                className={`v2-form-input ${formErrors.validFrom ? 'error' : ''}`}
                name="validFrom"
                value={formValues.validFrom}
                onChange={onChangeInput}
              />
              {formErrors.validFrom && (
                <span className="error-label">{formErrors.validFrom}</span>
              )}
            </div>
            <div className="v2-form-group" ref={assignRef('validTo')}>
              <label className="v2-form-label">
                Valid To <span className="v2-form-required">*</span>
              </label>
              <input
                type="date"
                className={`v2-form-input ${formErrors.validTo ? 'error' : ''}`}
                name="validTo"
                value={formValues.validTo}
                onChange={onChangeInput}
                min={formValues.validFrom || ''}
              />
              {formErrors.validTo && (
                <span className="error-label">{formErrors.validTo}</span>
              )}
            </div>
          </div>

          <div className="v2-form-group v2-form-group--toggle">
            <div className="v2-form-toggle-label">
              <label className="v2-form-label">Plan Status</label>
              <p className="v2-form-hint">
                Active plans are available for selection during onboarding
              </p>
            </div>
            <button
              type="button"
              className={`v2-form-toggle ${formValues.isActive ? 'v2-form-toggle--active' : ''}`}
              onClick={() => setFormValues((prev) => ({ ...prev, isActive: !prev.isActive }))}
            >
              <span className="v2-form-toggle__slider"></span>
            </button>
          </div>

          <div className="v2-plan-creation__actions">
            <button
              type="button"
              className="v2-btn v2-btn--secondary"
              onClick={() => handleSubmitPlan(1)}
              disabled={postPutLoading}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              {postPutLoading ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              type="button"
              className="v2-btn v2-btn--primary"
              onClick={() => handleSubmitPlan(0)}
              disabled={postPutLoading}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              {postPutLoading ? 'Publishing...' : 'Publish Plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanCreationForm;
